<template>
  <v-card>
    <v-card-title>
      <h4>{{ data.recipe.name }} - {{ data.recipe.process.name }}</h4>
      <comments-badge
        :edit="edit"
        :commentState="commentState"
      />
      <v-btn
        v-if="!edit && recipe.unique_id"
        icon
        flat
        color="grey"
        :href="trialRecipePdfUrl"
        target="_blank"
      >
        <v-icon dark>print</v-icon>
      </v-btn>
    </v-card-title>
    <template v-if="edit">
      <v-divider></v-divider>
      <v-container class="pa-1">
        <v-layout align-center justify-center row fill-height>
          <v-flex class="text-xs-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="success"
                  v-on="on"
                  @click="editRecipe(data.recipe.id)"
                >
                  settings
                </v-icon>
              </template>
              <span>Edit recipe</span>
            </v-tooltip>
          </v-flex>

          <v-flex
            class="text-xs-center"
            v-if="recipesCount < maxRecipesCount"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="warning"
                  v-on="on"
                  @click="copyRecipe(data.recipe.id)"
                >
                  file_copy
                </v-icon>
              </template>
              <span>Copy recipe</span>
            </v-tooltip>
          </v-flex>

          <v-flex class="text-xs-center">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-icon
                  small
                  color="error"
                  v-on="on"
                  @click="removeRecipe(data.recipe.id)"
                >
                  close
                </v-icon>
              </template>
              <span>Delete recipe</span>
            </v-tooltip>
          </v-flex>
        </v-layout>
      </v-container>
    </template>
    <template>
      <v-divider></v-divider>
      <table class="pa-3 mb-2" width="100%">
        <thead>
        <tr>
          <th width="33%" class="text-xs-left">
            <small>
              TARGET
            </small>
          </th>
          <th class="text-xs-center">
            <small>
              VALUE, KG
            </small>
          </th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td class="text-xs-left">
            <small>
              <b>Product needed</b>
            </small>
          </td>
          <td class="text-xs-center">
            <v-text-field
              type="number"
              :readonly="!edit"
              min="0"
              :step="PRECISION_STEP"
              v-model.number="data.recipe.product_needed"
              color="success"
              class="pa-0 ma-0 text-xs-center"
              reverse
              hide-details
            />
          </td>
        </tr>
        <tr v-show="permissions.RECIPE_MODIFY_LOOSES">
          <td class="text-xs-left">
            <small>
              <b>Losses</b>
            </small>
          </td>
          <td class="text-xs-center">
            <v-text-field
              type="number"
              :readonly="!(edit || permissions.RECIPE_MODIFY_LOOSES)"
              min="0"
              :step="PRECISION_STEP"
              v-model.number="data.recipe.losses"
              color="success"
              class="pa-0 ma-0"
              reverse
              hide-details
            />
          </td>
        </tr>
        <tr>
          <td class="text-xs-left pt-2">
            <small>
              <b>Batch</b>
            </small>
          </td>
          <td class="text-xs-center pt-2">
            <small>
              <b>{{ data.recipe.batch }}</b>
            </small>
          </td>
        </tr>
        </tbody>
      </table>
    </template>
    <template v-if="showRecipeDetail">
      <recipe-data-table
        :data="data.recipe.parameters"
        :headers="['PARAMETERS', 'TARGET, %', 'RESULT, %']"
        v-if="data.recipe.parameters.length"
      >
        <template v-slot:target="{ item }">
          <v-text-field
            type="number"
            :readonly="!(edit || permissions.RECIPE_MODIFY_VALUES)"
            min="0"
            :step="PRECISION_STEP"
            v-model.lazy.number="item.target"
            color="success"
            class="pa-0 ma-0 mr-3 ml-3"
            reverse
            hide-details
          />
        </template>
      </recipe-data-table>

      <recipe-data-table
        :data="data.recipe.ingredients"
        :headers="['INGREDIENTS', 'TARGET, %', 'RESULT, KG, L']"
        v-if="data.recipe.ingredients.length"
      >
        <template v-slot:name="{ item }">
          <v-checkbox
            v-model="item.isBase"
            @change="updateBaseIngredients(item.id)"
            :readonly="!(edit || permissions.RECIPE_MODIFY_VALUES)"
            color="success"
            class="ma-0"
            hide-details
          >
            <template v-slot:label>
              <small>
                <b>{{ item.name }}</b>
              </small>
            </template>
          </v-checkbox>

          <v-checkbox
            v-model="item.isStatic"
            :readonly="!(edit || permissions.RECIPE_MODIFY_VALUES) || item.isBase"
            color="warning"
            class="ma-0"
            hide-details
          >
            <template v-slot:label>
              <small>(fixed)</small>
            </template>
          </v-checkbox>

        </template>
        <template v-slot:target="{ item }">
          <v-text-field
            type="number"
            :readonly="!(edit || permissions.RECIPE_MODIFY_VALUES)"
            :disabled="item.isBase"
            min="0"
            :step="PRECISION_STEP"
            v-model.lazy.number="item.target"
            color="success"
            class="pa-0 ma-0 mr-3 ml-3"
            :class="{ error: item.isBase && item.target < 0 }"
            :dark="item.isBase && item.target < 0"
            reverse
            hide-details
          />
        </template>
      </recipe-data-table>

      <template>
        <v-divider></v-divider>
        <table width="100%" class="pa-3">
          <tbody>
          <tr>
            <td width="40%" class="text-xs-left">
              <small>
                <b>TOTAL</b>
              </small>
            </td>
            <td
              width="33%"
              class="text-xs-center"
              :class="{'error white--text': totalIngredientsTarget > 100 }"
            >
              <b>{{ totalIngredientsTarget }}</b>
            </td>
            <td
              width="33%"
              class="text-xs-center"
              :class="{'error white--text': totalIngredientsResult > data.recipe.product_needed }"
            >
              <b>{{ totalIngredientsResult }}</b>
            </td>
          </tr>
          </tbody>
        </table>
      </template>

      <recipe-data-table
        :data="data.recipe.ferments"
        :headers="['FERMENTS', 'TARGET, 0.001 %', 'RESULT, G, ML']"
        v-if="data.recipe.ferments.length"
      >
        <template v-slot:name="{ item }">
          <v-checkbox
            v-model="item.isBase"
            :readonly="!(edit || permissions.RECIPE_MODIFY_VALUES)"
            color="success"
            class="ma-0"
            hide-details
          >
            <template v-slot:label>
              <small>
                <b>{{ item.name }}</b>
              </small>
            </template>
          </v-checkbox>
        </template>
        <template v-slot:target="{ item }">
          <v-text-field
            type="number"
            :readonly="!(edit || permissions.RECIPE_MODIFY_VALUES)"
            min="0"
            :step="PRECISION_STEP"
            v-model.lazy.number="item.target"
            color="success"
            class="pa-0 ma-0 mr-3 ml-3"
            reverse
            hide-details
          />
        </template>
        <template v-slot:result="{ item }">
          <small>
            <b>{{ convertToGramm(item.result) }}</b>
          </small>
        </template>
      </recipe-data-table>

      <template v-if="data.recipe.ferments.length">
        <v-divider></v-divider>
        <table width="100%" class="pa-3">
          <tbody>
          <tr>
            <td width="40%" class="text-xs-left">
              <small>
                <b>TOTAL</b>
              </small>
            </td>
            <td
              width="33%"
              class="text-xs-center"
              :class="{'error white--text': totalFermentsTarget > 100 }"
            >
              <b>{{ totalFermentsTarget }}</b>
            </td>
            <td
              width="33%"
              class="text-xs-center"
              :class="{'error white--text': totalFermentsResult > data.recipe.product_needed }"
            >
              <b>{{ totalFermentsResult }}</b>
            </td>
          </tr>
          </tbody>
        </table>
      </template>

      <v-container fluid grid-list-xl class="pt-0">
        <v-layout wrap align-top justify-center>
          <v-btn
            @click="updateTargets"
            :loading="isUpdatedTargets"
            :disabled="isUpdatedTargets || !(edit || permissions.RECIPE_MODIFY_VALUES)"
            color="success"
            block
          >
            Update targets
          </v-btn>
        </v-layout>
      </v-container>

      <v-container v-if="save" fluid grid-list-xl class="pt-0">
        <v-layout wrap align-top justify-end>
          <v-btn
            @click="resetRecipe"
            flat
            small
            color="warning"
          >
            Reset
          </v-btn>
          <v-btn
            @click="updateRecipe"
            small
            color="success"
          >
            Save
          </v-btn>
        </v-layout>
      </v-container>
    </template>
  </v-card>
</template>

<script>
import recipeDataTable from './recipeDataTable';
import Recipes from '@/orm/models/Recipes';
import Alert from '@/orm/models/Alert';
import commentsBadge from '@/components/comments/commentsBadge';
import { ROUND_UP } from '@/utils';
import { API } from '@/utils/api';

export default {
  name: 'recipe',
  props: {
    recipe: {
      type: Object,
      default: () => new Recipes(),
    },
    edit: {
      type: Boolean,
      default: () => false,
    },
    maxRecipesCount: Number,
    recipesCount: Number,
    permissions: Object,
  },
  data() {
    return {
      data: {
        recipe: {},
      },
      PRECISION: 10000000,
      FERMENT_PRECISION: 1000,
      PRECISION_STEP: 0.1,
      save: false,
      selectedBaseIngredientId: null,
      isUpdatedTargets: false,
    };
  },
  watch: {
    'recipe': {
      immediate: true,
      handler: function(nRecipe) {
        this.data.recipe = JSON.parse(JSON.stringify(nRecipe));
        this.$nextTick(() => {
          this.save = false;
        });
      },
    },
    'data.recipe': {
      deep: true,
      handler: async function() {
        this.save = true;
        this.updateRecipeBatch();
        this.updateComponentsResults();
        this.updateParametersResults();
      },
    },
  },
  computed: {
    commentState() {
      return {
        module: 'RECIPE',
        id: this.$route.params.id,
        type: 'trials',
        uniqueId: this.recipe.unique_id,
      };
    },
    showRecipeDetail() {
      const { parameters, ingredients, ferments } = this.data.recipe;
      return parameters.length || ingredients || ferments;
    },
    totalIngredientsTarget() {
      return this.data.recipe.ingredients
        .reduce((acc, item) => {
          if (!item.target) return acc;
          return ROUND_UP(acc + item.target, this.PRECISION);
        }, 0);
    },
    totalIngredientsResult() {
      return this.data.recipe.ingredients
        .reduce((acc, item) => {
          if (!item.result) return acc;
          return ROUND_UP(acc + item.result, this.PRECISION);
        }, 0);
    },
    totalFermentsTarget() {
      return this.data.recipe.ferments
        .reduce((acc, item) => {
          if (!item.target) return acc;
          return ROUND_UP(acc + item.target, this.PRECISION);
        }, 0);
    },
    totalFermentsResult() {
      return this.data.recipe.ferments
        .reduce((acc, item) => {
          if (!item.result) return acc;
          return ROUND_UP(acc + item.result, this.PRECISION);
        }, 0);
    },
    trialRecipePdfUrl() {
      return '/api/v2/pdf/trial_recipe_single/' + this.$route.params.id + '/' + this.recipe.unique_id;
    }
  },
  methods: {
    editRecipe(id) {
      this.$emit('editRecipe', id);
    },
    async copyRecipe(recipeId) {
      const newItem = Recipes.find(+recipeId).$toJson();
      newItem.id = null;
      newItem.unique_id = '';
      await Recipes.insert({ data: JSON.parse(JSON.stringify(newItem)) });
    },
    async removeRecipe(recipeId) {
      await Recipes.delete(recipeId);
    },
    updateBaseIngredients(id) {
      this.data.recipe.ingredients.forEach(ing => {ing.isBase = ing.id === +id; ing.isStatic = false});
      this.updateBaseIngredientsTarget();
    },
    updateBaseIngredientsTarget() {
      const ingredient = this.data.recipe.ingredients.find(ing => ing.isBase);
      if (!ingredient) return;
      const total = this.data.recipe.ingredients
        .reduce((acc, item) => {
          if (!item.target || item.isBase) return acc;
          return ROUND_UP(acc + item.target, this.PRECISION);
        }, 0);
      ingredient.target = ROUND_UP(100 - total, this.PRECISION);
    },
    updateComponentsResults() {
      this.data.recipe.ingredients.forEach(this.changeResultIngredient);
      this.updateBaseIngredientsTarget();
      this.data.recipe.ferments.forEach(this.changeResultFerment);
    },
    updateParametersResults() {
      this.data.recipe.parameters.forEach(parameter => {
        const resultIngredients = this.data.recipe.ingredients.reduce((acc, ing) => {
          const ingTarget = ROUND_UP(ing.target / 100, this.PRECISION);
          const paramValue = ing.selectedParameters
            .find(p => p.name === parameter.name).value;
          const mul = ROUND_UP(ingTarget * paramValue, this.PRECISION);
          return ROUND_UP(acc + mul, this.PRECISION);
        }, 0);

        const resultFerments = this.data.recipe.ferments.reduce((acc, fer) => {
          if (!fer.isBase) return acc;

          const ferTarget = ROUND_UP(fer.target / this.FERMENT_PRECISION, this.PRECISION);
          const paramValue = fer.selectedParameters
            .find(p => p.name === parameter.name).value;
          const mul = ROUND_UP(ferTarget * paramValue, this.PRECISION);
          return ROUND_UP(acc + mul, this.PRECISION);
        }, 0);
        parameter.result = ROUND_UP(resultIngredients + resultFerments, this.PRECISION);
      });
    },
    resetRecipe() {
      this.data.recipe = JSON.parse(JSON.stringify(this.recipe));
      this.$nextTick(() => {
        this.save = false;
      });
    },
    async updateRecipe() {
      await Recipes.insertOrUpdate({
        data: JSON.parse(JSON.stringify(this.data.recipe)),
      });
    },
    updateRecipeBatch() {
      const { product_needed, losses } = this.data.recipe;
      const PRECISION = this.PRECISION;
      this.data.recipe.batch = ROUND_UP(product_needed + losses, PRECISION);
    },
    changeResultIngredient(ingredient) {
      const { product_needed } = this.data.recipe;
      const PRECISION = this.PRECISION;
      ingredient.result = ROUND_UP((ingredient.target * product_needed) / 100, PRECISION);
    },
    changeResultFerment(ferment) {
      const { product_needed } = this.data.recipe;
      const PRECISION = this.PRECISION;
      const FERMENT_PRECISION = this.FERMENT_PRECISION;
      const fermentTarget = ROUND_UP(ferment.target / FERMENT_PRECISION, PRECISION);
      ferment.result = ROUND_UP((fermentTarget * product_needed) / 100, PRECISION);
    },
    convertToGramm(val) {
      return ROUND_UP(val * this.FERMENT_PRECISION, this.PRECISION);
    },
    async updateTargets() {
      try {
        this.isUpdatedTargets = true;
        const postData = Object
          .entries(this.data.recipe)
          .filter(([key]) => {
            return ![
              'process_id',
              'process',
              'name',
              'graph',
            ].includes(key) && !key.includes('$');
          }).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        let result = await API.post(
          '/utils/calculate_dosage',
          postData,
        );

        let recipe = this.data.recipe;
        result.data.ingredients.forEach((ingredient, index) => {
            recipe.ingredients[index].target = ingredient.target;
        });

      }

      catch (e) {
        if (e.response && e.response.status >= 400 && e.response.status < 500) {
          let errorMsg = e.response.data.error || 'Ошибка при расчете рецептуры';
          await Alert.create({
            data: {
              message: errorMsg,
              error: true,
              errorTip: 'Попробуйте изменить входные параметры.'
            },
          });
        } else {
          await Alert.create({
            data: {
              message: 'Произошла неизвестная ошибка',
              error: true,
              errorTip: 'Возможно сервер недоступен. Попробуйте позже.'
            },
          });
        }
      }

      finally {
        this.isUpdatedTargets = false;
      }
    },
  },
  components: {
    recipeDataTable,
    commentsBadge,
  },
};</script>
